import 'react-native-get-random-values'

import React, { useState } from 'react'
import { Alert, ScrollView, ImageBackground, StyleSheet, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Button, List, Divider, Chip } from 'react-native-paper'
import { useRouter } from 'expo-router'
import { useAction } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { debounce } from 'lodash'
import { observer } from 'mobx-react-lite'
import { evaluateHand, findWinners } from '@ppwr/poker-engine'
import type { Card, EnginePlayer } from '@ppwr/poker-engine'

import image from '../../../assets/icons/Home_Purple.png'
import styles from '../../styles/globalStyle'
import { DS } from '../../design-system'
import { useStore } from '../../models'

const startStyles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column'
  },
  image: {
    flex: 1,
    resizeMode: 'cover',
    justifyContent: 'center'
  }
})

// ---------------------------------------------------------------------------
// Game scenario types (existing)
// ---------------------------------------------------------------------------

type ScenarioItem = {
  key: string
  name: string
  description: string
  playerCount: number
  chipAmounts: number[]
  botStrategy?: string
  deckMode?: 'random' | 'fixed'
}

const SIDEPOT_TEST_SCENARIOS: ScenarioItem[] = [
  {
    key: 'splitStraights',
    name: 'Split Straights',
    description: 'You + 2 bots: bots share Ace-high straights (chop); you have high card only',
    playerCount: 3,
    chipAmounts: [500, 500, 500],
    botStrategy: 'progressive',
    deckMode: 'fixed'
  },

  // Single-round all-in scenarios
  {
    key: 'headsUp',
    name: 'Heads-Up',
    description: 'Two players with different stacks (300 vs 500)',
    playerCount: 2,
    chipAmounts: [300, 500]
  },
  {
    key: 'basic3Player',
    name: 'Basic 3-Player',
    description: 'Short stack (500) vs two equal stacks (1000)',
    playerCount: 3,
    chipAmounts: [500, 1000, 1000]
  },
  {
    key: 'equalStacks',
    name: 'Equal Stacks (No Sidepots)',
    description: 'All equal (500) - single pot only',
    playerCount: 3,
    chipAmounts: [500, 500, 500]
  },
  {
    key: 'microStack',
    name: 'Micro Stack Edge Case',
    description: 'Very small stack (50) vs normal stacks',
    playerCount: 3,
    chipAmounts: [50, 1000, 1000]
  },
  {
    key: 'cascading4Player',
    name: 'Cascading 4-Player',
    description: 'Creates 3 side pots with cascading all-ins',
    playerCount: 4,
    chipAmounts: [200, 500, 1000, 2000]
  },
  {
    key: 'complex6Player',
    name: '6-Player Max Complexity',
    description: 'Maximum complexity with 5 potential side pots',
    playerCount: 6,
    chipAmounts: [100, 250, 500, 750, 1000, 2000]
  },

  // Scenarios for new engine fixes
  {
    key: 'uncalledBet',
    name: 'Uncalled Bet Return',
    description: 'Big stack (800) all-in vs two 200s — excess 600 returned to big stack',
    playerCount: 3,
    chipAmounts: [200, 200, 800]
  },
  {
    key: 'partialBlind',
    name: 'Partial Blind (SB Short)',
    description: 'SB only has 15 chips (blinds 50/100) — tests partial blind + side pot creation',
    playerCount: 3,
    chipAmounts: [500, 500, 15],
    botStrategy: 'progressive'
  },
  {
    key: 'partialBlindBB',
    name: 'Partial Blind (BB Short)',
    description: 'BB only has 15 chips (blinds 50/100) — tests partial blind from BB position',
    playerCount: 3,
    chipAmounts: [500, 15, 500],
    botStrategy: 'progressive'
  },
  {
    key: 'partialBlindDealer',
    name: 'Partial Blind (D Short)',
    description: 'Dealer only has 15 chips (blinds 50/100) — tests short stack on the button',
    playerCount: 3,
    chipAmounts: [15, 500, 500],
    botStrategy: 'progressive'
  },

  // Multi-round progressive scenarios (fixed deck = deterministic outcomes)
  {
    key: 'multiRound4Player',
    name: 'Multi-Round 4-Player',
    description: 'Progressive: 4 players through multiple streets, cascading all-ins on turn',
    playerCount: 4,
    chipAmounts: [300, 600, 1000, 2000],
    botStrategy: 'progressive'
  },
  {
    key: 'multiRoundFixedDeck3P',
    name: 'Multi-Round Fixed Deck (3P)',
    description: 'Deterministic: AA vs KK vs QQ, progressive all-in on turn',
    playerCount: 3,
    chipAmounts: [500, 1000, 1500],
    botStrategy: 'progressive',
    deckMode: 'fixed'
  },
  {
    key: 'multiRoundFixedDeck4P',
    name: 'Multi-Round Fixed Deck (4P)',
    description: 'Deterministic: flush draw vs top pair vs pair vs straight draw',
    playerCount: 4,
    chipAmounts: [400, 800, 1200, 2000],
    botStrategy: 'progressive',
    deckMode: 'fixed'
  },
  {
    key: 'splitPotFixed',
    name: 'Split Pot Fixed Deck (3P)',
    description: 'Deterministic: board straight forces a 3-way split pot',
    playerCount: 3,
    chipAmounts: [500, 500, 500],
    botStrategy: 'progressive',
    deckMode: 'fixed'
  },
  {
    key: 'foldTopSidepot',
    name: 'Fold Top Sidepot (4P)',
    description: 'Big stack folds → top side pot won by fold, lower pots + main won by aces (seat 1)',
    playerCount: 4,
    chipAmounts: [300, 800, 1500, 2500],
    botStrategy: 'progressive',
    deckMode: 'fixed'
  }
]

// ---------------------------------------------------------------------------
// Winning Scenarios — deterministic hand evaluation test runner
// ---------------------------------------------------------------------------

type HandTestCase = {
  seatLabel: string
  hole: [string, string]
  expectedRank: string
}

type WinningScenarioSpec = {
  key: string
  name: string
  description: string
  board: [string, string, string, string, string]
  seats: HandTestCase[]
  expectedWinnerSeats: number[]
  isSplit: boolean
}

const WINNING_SCENARIOS: WinningScenarioSpec[] = [
  {
    key: 'splitStraights',
    name: 'Split Straights',
    description: 'Board: Qd Js Tc 3h 2d — two AK straights chop; high card loses',
    board: ['Qd', 'Js', 'Tc', '3h', '2d'],
    seats: [
      { seatLabel: 'Seat 0 (7c 8d)', hole: ['7c', '8d'], expectedRank: 'High Card' },
      { seatLabel: 'Seat 1 (Ac Kc)', hole: ['Ac', 'Kc'], expectedRank: 'Straight' },
      { seatLabel: 'Seat 2 (Ah Kh)', hole: ['Ah', 'Kh'], expectedRank: 'Straight' }
    ],
    expectedWinnerSeats: [1, 2],
    isSplit: true
  },
  {
    key: 'acesVsKingsVsQueens',
    name: 'AA vs KK vs QQ',
    description: 'Board: Jd Td 5c 8d 7d — pocket pairs, best kicker wins',
    board: ['Jd', 'Td', '5c', '8d', '7d'],
    seats: [
      { seatLabel: 'Seat 0 (Ah As)', hole: ['Ah', 'As'], expectedRank: 'Pair' },
      { seatLabel: 'Seat 1 (Kh Ks)', hole: ['Kh', 'Ks'], expectedRank: 'Pair' },
      { seatLabel: 'Seat 2 (Qh Qs)', hole: ['Qh', 'Qs'], expectedRank: 'Pair' }
    ],
    expectedWinnerSeats: [0],
    isSplit: false
  },
  {
    key: 'fourPlayerShowdown',
    name: 'Flush vs Straight vs Trips vs Two Pair',
    description: 'Board: Ah 5h Tc Kh 2h — heart flush takes it',
    board: ['Ah', '5h', 'Tc', 'Kh', '2h'],
    seats: [
      { seatLabel: 'Seat 0 (9h 7h)', hole: ['9h', '7h'], expectedRank: 'Flush' },
      { seatLabel: 'Seat 1 (Ad Kd)', hole: ['Ad', 'Kd'], expectedRank: 'Two Pair' },
      { seatLabel: 'Seat 2 (Ts Td)', hole: ['Ts', 'Td'], expectedRank: 'Three of a Kind' },
      { seatLabel: 'Seat 3 (Jc Qc)', hole: ['Jc', 'Qc'], expectedRank: 'Straight' }
    ],
    expectedWinnerSeats: [0],
    isSplit: false
  },
  {
    key: 'splitPot',
    name: 'Board Straight — 3-Way Chop',
    description: 'Board: 5d 6h 7c 8s 9d — all players play the board straight',
    board: ['5d', '6h', '7c', '8s', '9d'],
    seats: [
      { seatLabel: 'Seat 0 (2h 3h)', hole: ['2h', '3h'], expectedRank: 'Straight' },
      { seatLabel: 'Seat 1 (2d 3d)', hole: ['2d', '3d'], expectedRank: 'Straight' },
      { seatLabel: 'Seat 2 (2s 3s)', hole: ['2s', '3s'], expectedRank: 'Straight' }
    ],
    expectedWinnerSeats: [0, 1, 2],
    isSplit: true
  },
  {
    key: 'foldTopSidepot',
    name: 'AA dominates — Fold Top Sidepot',
    description: 'Board: 9d 5h Tc 8d 3d — aces beat kings, queens, and junk',
    board: ['9d', '5h', 'Tc', '8d', '3d'],
    seats: [
      { seatLabel: 'Seat 0 (7d 2c)', hole: ['7d', '2c'], expectedRank: 'High Card' },
      { seatLabel: 'Seat 1 (Ah As)', hole: ['Ah', 'As'], expectedRank: 'Pair' },
      { seatLabel: 'Seat 2 (Kh Ks)', hole: ['Kh', 'Ks'], expectedRank: 'Pair' },
      { seatLabel: 'Seat 3 (Qh Qs)', hole: ['Qh', 'Qs'], expectedRank: 'Pair' }
    ],
    expectedWinnerSeats: [1],
    isSplit: false
  }
]

/** Sidepot scenario keys that use the same fixed deck as each winning scenario (Convex `SIDEPOT_SCENARIOS`). */
const WINNING_SCENARIO_GAME_KEY: Record<string, string> = {
  splitStraights: 'splitStraights',
  acesVsKingsVsQueens: 'multiRoundFixedDeck3P',
  fourPlayerShowdown: 'multiRoundFixedDeck4P',
  splitPot: 'splitPotFixed',
  foldTopSidepot: 'foldTopSidepot'
}

type CaseResult = {
  seatLabel: string
  hole: [string, string]
  actualRank: string
  expectedRank: string
  passed: boolean
}

type ScenarioTestResult = {
  key: string
  name: string
  rankTestsPassed: number
  rankTestsTotal: number
  winnerPassed: boolean
  actualWinnerSeats: number[]
  expectedWinnerSeats: number[]
  caseResults: CaseResult[]
  allPassed: boolean
}

function runWinningTestForScenario(scenario: WinningScenarioSpec): ScenarioTestResult {
  const board = scenario.board as unknown as Card[]

  const caseResults: CaseResult[] = scenario.seats.map((seat) => {
    const result = evaluateHand(seat.hole as unknown as Card[], board)
    return {
      seatLabel: seat.seatLabel,
      hole: seat.hole,
      actualRank: result.rank,
      expectedRank: seat.expectedRank,
      passed: result.rank === seat.expectedRank
    }
  })

  const players: EnginePlayer[] = scenario.seats.map((seat, i) => ({
    id: `p${i}`,
    userId: `p${i}`,
    name: seat.seatLabel,
    chips: 1000,
    currentBet: 0,
    cards: seat.hole as unknown as Card[],
    state: 'active' as const,
    hasActedThisRound: false,
    seatId: i,
    position: 'none' as const
  }))

  const winners = findWinners(players, board)
  const actualWinnerSeats = winners.map((w) => parseInt(w.playerId.replace('p', '')))
  const winnerPassed =
    actualWinnerSeats.length === scenario.expectedWinnerSeats.length &&
    actualWinnerSeats.every((s) => scenario.expectedWinnerSeats.includes(s))

  const rankTestsPassed = caseResults.filter((c) => c.passed).length
  const allPassed = rankTestsPassed === caseResults.length && winnerPassed

  return {
    key: scenario.key,
    name: scenario.name,
    rankTestsPassed,
    rankTestsTotal: caseResults.length,
    winnerPassed,
    actualWinnerSeats,
    expectedWinnerSeats: scenario.expectedWinnerSeats,
    caseResults,
    allPassed
  }
}

function runWinningTests(): ScenarioTestResult[] {
  return WINNING_SCENARIOS.map((scenario) => runWinningTestForScenario(scenario))
}

// ---------------------------------------------------------------------------
// WinningScenarioCard — extracted to keep component complexity manageable
// ---------------------------------------------------------------------------

type WinningScenarioCardProps = {
  scenario: WinningScenarioSpec
  result: ScenarioTestResult | undefined
  isExpanded: boolean
  onToggleExpand: () => void
  sidepotGameKey: string | undefined
  onCreateGame?: () => void | Promise<void>
  onRunTest?: () => void
  creatingGame?: boolean
  disableActions?: boolean
}

const WinningScenarioHeader = ({
  scenario,
  result
}: {
  scenario: WinningScenarioSpec
  result: ScenarioTestResult | undefined
}) => (
  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
    <View style={{ flex: 1 }}>
      <DS.Text variant="Body Small/Medium">{scenario.name}</DS.Text>
      <DS.Text variant="Caption/Regular" style={{ color: '#666', marginTop: 2 }}>
        {scenario.description}
      </DS.Text>
    </View>
    {result && (
      <View style={{ alignItems: 'flex-end', marginLeft: 8 }}>
        <DS.Text variant="Caption/Bold" style={{ color: result.allPassed ? '#2e7d32' : '#c62828' }}>
          {result.allPassed ? 'PASS' : 'FAIL'}
        </DS.Text>
        <DS.Text variant="Caption/Regular" style={{ color: '#888' }}>
          {result.rankTestsPassed}/{result.rankTestsTotal} ranks
        </DS.Text>
      </View>
    )}
  </View>
)

const WinningScenarioBoardRow = ({ board }: { board: WinningScenarioSpec['board'] }) => (
  <View style={{ flexDirection: 'row', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
    <DS.Text variant="Caption/Regular" style={{ color: '#888' }}>
      Board:
    </DS.Text>
    {board.map((card) => (
      <Chip key={card} compact textStyle={{ fontSize: 10 }} style={{ backgroundColor: '#f5f5f5' }}>
        {card}
      </Chip>
    ))}
  </View>
)

const WinningScenarioActionRow = ({
  sidepotGameKey,
  onRunTest,
  onCreateGame,
  creatingGame,
  disableActions
}: {
  sidepotGameKey: string | undefined
  onRunTest?: () => void
  onCreateGame?: () => void | Promise<void>
  creatingGame?: boolean
  disableActions?: boolean
}) => {
  const canCreateGame = sidepotGameKey != null && onCreateGame != null
  const shouldShowRow = Boolean(onRunTest) || canCreateGame
  if (!shouldShowRow) return null
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
      {onRunTest && (
        <Button mode="outlined" compact onPress={onRunTest} disabled={disableActions}>
          Run test
        </Button>
      )}
      {sidepotGameKey && onCreateGame && (
        <Button mode="contained" compact loading={creatingGame} disabled={disableActions} onPress={onCreateGame}>
          Create game
        </Button>
      )}
    </View>
  )
}

const WinningScenarioExpectedWinnerRow = ({
  scenario,
  result
}: {
  scenario: WinningScenarioSpec
  result: ScenarioTestResult | undefined
}) => (
  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 6 }}>
    <DS.Text variant="Caption/Regular" style={{ color: '#888' }}>
      Expected winner:
    </DS.Text>
    {scenario.isSplit ? (
      <Chip compact icon="equal" textStyle={{ fontSize: 10 }} style={{ backgroundColor: '#e3f2fd' }}>
        {scenario.expectedWinnerSeats.length}-way split
      </Chip>
    ) : (
      <Chip compact icon="trophy" textStyle={{ fontSize: 10 }} style={{ backgroundColor: '#fff9c4' }}>
        {scenario.seats[scenario.expectedWinnerSeats[0]].seatLabel}
      </Chip>
    )}
    {result && !result.winnerPassed && (
      <DS.Text variant="Caption/Regular" style={{ color: '#c62828' }}>
        got seat(s) {result.actualWinnerSeats.join(', ')}
      </DS.Text>
    )}
  </View>
)

const WinningScenarioExpandedDetails = ({ result }: { result: ScenarioTestResult }) => (
  <View style={{ borderTopWidth: 1, borderTopColor: '#f0f0f0', paddingHorizontal: 12, paddingBottom: 12 }}>
    {result.caseResults.map((cr) => (
      <View
        key={cr.seatLabel}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingVertical: 6,
          borderBottomWidth: 1,
          borderBottomColor: '#f5f5f5'
        }}
      >
        <DS.Text
          variant="Caption/Regular"
          style={{ width: 14, color: cr.passed ? '#2e7d32' : '#c62828', fontWeight: '700' }}
        >
          {cr.passed ? '✓' : '✗'}
        </DS.Text>
        <View style={{ flex: 1, marginLeft: 6 }}>
          <DS.Text variant="Caption/Regular">{cr.seatLabel}</DS.Text>
          <DS.Text variant="Caption/Regular" style={{ color: '#888' }}>
            {cr.hole.join(' ')} → {cr.actualRank}
            {!cr.passed ? ` (expected ${cr.expectedRank})` : ''}
          </DS.Text>
        </View>
      </View>
    ))}
    <View style={{ marginTop: 8 }}>
      <DS.Text variant="Caption/Regular" style={{ color: '#888' }}>
        Winner check:{' '}
        <DS.Text variant="Caption/Bold" style={{ color: result.winnerPassed ? '#2e7d32' : '#c62828' }}>
          {result.winnerPassed ? 'PASS' : `FAIL — got seat(s) ${result.actualWinnerSeats.join(', ')}`}
        </DS.Text>
      </DS.Text>
    </View>
  </View>
)

const WinningScenarioCard = ({
  scenario,
  result,
  isExpanded,
  onToggleExpand,
  sidepotGameKey,
  onCreateGame,
  onRunTest,
  creatingGame,
  disableActions
}: WinningScenarioCardProps) => (
  <View
    style={{
      backgroundColor: '#fff',
      borderRadius: 8,
      marginBottom: 12,
      borderWidth: 1,
      borderColor: result ? (result.allPassed ? '#a5d6a7' : '#ffcc80') : '#e0e0e0'
    }}
  >
    <View style={{ padding: 12 }}>
      <WinningScenarioHeader scenario={scenario} result={result} />
      <WinningScenarioBoardRow board={scenario.board} />
      <WinningScenarioActionRow
        sidepotGameKey={sidepotGameKey}
        onRunTest={onRunTest}
        onCreateGame={onCreateGame}
        creatingGame={creatingGame}
        disableActions={disableActions}
      />
      <WinningScenarioExpectedWinnerRow scenario={scenario} result={result} />
      {result && (
        <Button mode="text" compact onPress={onToggleExpand} style={{ marginTop: 4, alignSelf: 'flex-start' }}>
          {isExpanded ? 'Hide details' : 'Show details'}
        </Button>
      )}
    </View>
    {isExpanded && result ? <WinningScenarioExpandedDetails result={result} /> : null}
  </View>
)

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AdminSidepotTestScreen = observer(() => {
  const router = useRouter()
  const store = useStore()
  const [isCreating, setIsCreating] = useState<string | null>(null)
  const [createdGames, setCreatedGames] = useState<Array<{ gameId: string; scenario: string }>>([])
  const [fastBots, setFastBots] = useState(true)
  const [testResults, setTestResults] = useState<ScenarioTestResult[] | null>(null)
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null)

  const createSidepotTestGame = useAction(api.testing.actions.createSidepotTestGame)

  const userId = store.session.user?.auth0Id
  const userName = store.session.user?.name || 'Test Admin'

  const handleCreateTestGame = async (scenarioKey: string, scenarioName: string) => {
    if (!userId) {
      Alert.alert('Error', 'You must be logged in to create a test game')
      return
    }

    setIsCreating(`sidepot:${scenarioKey}`)
    try {
      const result = await createSidepotTestGame({
        scenarioKey,
        userId,
        userName,
        startGame: true,
        autoPlay: true,
        fastBots
      })

      setCreatedGames((prev) => [{ gameId: result.gameId, scenario: scenarioName }, ...prev])

      router.push({
        pathname: '/game/[gameId]',
        params: { gameId: result.gameId }
      } as never)
    } catch (error) {
      Alert.alert('Error', error instanceof Error ? error.message : 'Failed to create game')
    } finally {
      setIsCreating(null)
    }
  }

  const handleCreateWinningScenarioGame = async (winningKey: string, displayName: string) => {
    const sidepotKey = WINNING_SCENARIO_GAME_KEY[winningKey]
    if (!sidepotKey) {
      Alert.alert('Error', 'No linked sidepot scenario for this winning scenario')
      return
    }
    await handleCreateTestGame(sidepotKey, `Winning: ${displayName}`)
  }

  const handleRunSingleWinningTest = (scenario: WinningScenarioSpec) => {
    setTestResults((prev) => {
      const next = new Map((prev ?? []).map((r) => [r.key, r]))
      next.set(scenario.key, runWinningTestForScenario(scenario))
      return WINNING_SCENARIOS.map((s) => next.get(s.key)).filter((x): x is ScenarioTestResult => x != null)
    })
    setExpandedScenario(scenario.key)
  }

  const handleRunWinningTests = () => {
    const results = runWinningTests()
    setTestResults(results)
    setExpandedScenario(null)
  }

  const getStrategyLabel = (item: ScenarioItem) => {
    if (item.botStrategy === 'progressive') return 'Multi-Round'
    return 'All-In'
  }

  const getStrategyColor = (item: ScenarioItem) => {
    if (item.botStrategy === 'progressive') return '#e3f2fd'
    return '#fce4ec'
  }

  const totalPassed = testResults?.filter((r) => r.allPassed).length ?? 0
  const totalScenarios = WINNING_SCENARIOS.length

  return (
    <ImageBackground source={image} style={startStyles.image}>
      <View style={{ backgroundColor: 'rgba(255,255,255,.9)', flex: 1 }}>
        <SafeAreaView style={styles.container}>
          {/* Back Button */}
          <View style={{ paddingHorizontal: 16, paddingTop: 8, alignItems: 'flex-start' }}>
            <Button mode="text" icon="arrow-left" onPress={() => router.back()}>
              Back
            </Button>
          </View>

          {/* Header */}
          <View style={{ padding: 16 }}>
            <DS.Text variant="Heading 3/Medium">Sidepot Testing</DS.Text>
            <DS.Text variant="Body Small/Regular" style={{ marginTop: 8, color: '#666' }}>
              Create test games with pre-configured chip stacks to test sidepot calculations and UI.
            </DS.Text>
          </View>

          {/* Bot Speed Toggle */}
          <View style={{ paddingHorizontal: 16, paddingBottom: 12 }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: fastBots ? '#e8f5e9' : '#f5f5f5',
                padding: 12,
                borderRadius: 8
              }}
            >
              <View style={{ flex: 1 }}>
                <DS.Text variant="Body Small/Medium">
                  Bot Speed: {fastBots ? 'Fast (0.5–1s)' : 'Regular (2–5s)'}
                </DS.Text>
              </View>
              <Button
                mode={fastBots ? 'contained' : 'outlined'}
                compact
                onPress={() => setFastBots(!fastBots)}
                style={{ marginLeft: 12 }}
              >
                {fastBots ? 'Fast' : 'Regular'}
              </Button>
            </View>
          </View>

          <Divider />

          {/* Main Scrollable Content */}
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 16 }}>
            {/* Available Scenarios */}
            <View style={{ padding: 16 }}>
              <DS.Text variant="Heading 5/Medium" style={{ marginBottom: 12 }}>
                Available Scenarios
              </DS.Text>
              {SIDEPOT_TEST_SCENARIOS.map((item) => (
                <List.Item
                  key={item.key}
                  title={item.name}
                  titleStyle={{ fontWeight: '600' }}
                  description={() => (
                    <View style={{ marginTop: 4 }}>
                      <DS.Text variant="Caption/Regular" style={{ color: '#666' }}>
                        {item.description}
                      </DS.Text>
                      <View style={{ flexDirection: 'row', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        <Chip compact icon="account-group" textStyle={{ fontSize: 11 }}>
                          You + {item.playerCount - 1} bot{item.playerCount - 1 !== 1 ? 's' : ''}
                        </Chip>
                        <Chip compact icon="poker-chip" textStyle={{ fontSize: 11 }}>
                          {item.chipAmounts.join(', ')}
                        </Chip>
                        <Chip
                          compact
                          icon={item.botStrategy === 'progressive' ? 'chart-timeline-variant' : 'lightning-bolt'}
                          textStyle={{ fontSize: 11 }}
                          style={{ backgroundColor: getStrategyColor(item) }}
                        >
                          {getStrategyLabel(item)}
                        </Chip>
                        {item.deckMode === 'fixed' && (
                          <Chip compact icon="lock" textStyle={{ fontSize: 11 }} style={{ backgroundColor: '#e8eaf6' }}>
                            Fixed Deck
                          </Chip>
                        )}
                      </View>
                    </View>
                  )}
                  right={() => (
                    <Button
                      mode="contained"
                      compact
                      loading={isCreating === `sidepot:${item.key}`}
                      disabled={isCreating !== null}
                      onPress={debounce(() => handleCreateTestGame(item.key, item.name), 250)}
                      style={{ alignSelf: 'center' }}
                    >
                      Create
                    </Button>
                  )}
                  style={{
                    backgroundColor: '#fff',
                    marginBottom: 12,
                    borderRadius: 8,
                    paddingVertical: 8
                  }}
                />
              ))}
            </View>

            {/* Recently Created */}
            {createdGames.length > 0 && (
              <>
                <Divider />
                <View style={{ padding: 16 }}>
                  <DS.Text variant="Heading 5/Medium" style={{ marginBottom: 8 }}>
                    Recently Created ({createdGames.length})
                  </DS.Text>
                  {createdGames.map((item, index) => (
                    <List.Item
                      key={`${item.gameId}-${index}`}
                      title={item.scenario}
                      description={`ID: ${item.gameId.slice(0, 12)}...`}
                      right={() => (
                        <Button
                          mode="outlined"
                          compact
                          onPress={debounce(
                            () =>
                              router.push({
                                pathname: '/game/[gameId]',
                                params: { gameId: item.gameId }
                              } as never),
                            250
                          )}
                        >
                          View
                        </Button>
                      )}
                      style={{ backgroundColor: '#f5f5f5', marginBottom: 4, borderRadius: 4 }}
                    />
                  ))}
                </View>
              </>
            )}

            <Divider />

            {/* Winning Scenarios — in-app test runner */}
            <View style={{ padding: 16 }}>
              {/* Section header */}
              <View
                style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}
              >
                <View style={{ flex: 1 }}>
                  <DS.Text variant="Heading 5/Medium">Winning Scenarios</DS.Text>
                  <DS.Text variant="Caption/Regular" style={{ color: '#666', marginTop: 2 }}>
                    Run a local hand check per scenario, or create the matching fixed-deck game to verify winners in the
                    UI. Cards are dealt in seat order (lowest seat first); you usually sit seat 0.
                  </DS.Text>
                </View>
                <Button
                  mode="contained"
                  compact
                  icon="play-circle"
                  onPress={handleRunWinningTests}
                  style={{ marginLeft: 12 }}
                >
                  Run All
                </Button>
              </View>

              {/* Summary badge after run */}
              {testResults !== null && (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: totalPassed === totalScenarios ? '#e8f5e9' : '#fff3e0',
                    borderRadius: 8,
                    padding: 10,
                    marginBottom: 12
                  }}
                >
                  <DS.Text variant="Body Small/Medium" style={{ flex: 1 }}>
                    {totalPassed === totalScenarios
                      ? `All ${totalScenarios} scenarios passed`
                      : `${totalPassed} / ${totalScenarios} scenarios passed`}
                  </DS.Text>
                  <DS.Text
                    variant="Body Small/Medium"
                    style={{ color: totalPassed === totalScenarios ? '#2e7d32' : '#e65100' }}
                  >
                    {totalPassed === totalScenarios ? 'PASS' : 'FAIL'}
                  </DS.Text>
                </View>
              )}

              {/* One card per scenario */}
              {WINNING_SCENARIOS.map((scenario) => {
                const result = testResults?.find((r) => r.key === scenario.key)
                const isExpanded = expandedScenario === scenario.key
                const sidepotGameKey = WINNING_SCENARIO_GAME_KEY[scenario.key]
                return (
                  <WinningScenarioCard
                    key={scenario.key}
                    scenario={scenario}
                    result={result}
                    isExpanded={isExpanded}
                    onToggleExpand={() => setExpandedScenario(isExpanded ? null : scenario.key)}
                    sidepotGameKey={sidepotGameKey}
                    onRunTest={() => handleRunSingleWinningTest(scenario)}
                    onCreateGame={
                      sidepotGameKey
                        ? async () => {
                            await handleCreateWinningScenarioGame(scenario.key, scenario.name)
                          }
                        : undefined
                    }
                    creatingGame={isCreating === `sidepot:${sidepotGameKey}`}
                    disableActions={isCreating !== null}
                  />
                )
              })}
            </View>

            {/* Info Section */}
            <View style={{ padding: 16, backgroundColor: '#f0f0f0' }}>
              <DS.Text variant="Caption/Bold" style={{ marginBottom: 4 }}>
                How it works:
              </DS.Text>
              <DS.Text variant="Caption/Regular" style={{ color: '#666' }}>
                {
                  '1. Select a scenario to create a test game\n2. Game runs automatically with the configured bot strategy\n3. All-In scenarios: instant all-in. Multi-Round: play through streets first\n4. Use sidepots drawer to inspect pot structure\n5. Winning Scenarios: use Run test / Run All for local checks, or Create game to open the same fixed-deck sidepot scenario'
                }
              </DS.Text>
            </View>
          </ScrollView>
        </SafeAreaView>
      </View>
    </ImageBackground>
  )
})
